import type { ReactElement } from "react"
import ItemDescriptionBoxMol from "@onestore/hel/dist/components/molecules/ItemDescriptionBoxMol"
import { TileMolLinkBox } from "@onestore/hel/dist/components/molecules/TileMol/TileMol.styled"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import TilesOrg from "@onestore/hel/dist/components/organisms/TilesOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type {
  Tile,
  TilesSectionFlatData,
} from "@gatsby-plugin-generic-page/fragments/tilesSection"
import getBorderRadiusSize from "@gatsby-plugin-generic-page/helpers/getBorderRadiusSize"
import { getActionTypeData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import isEmpty from "~/lib/isEmpty"
import type { Pages } from "~/types/squidex"

type CtaLinkProps = {
  title?: string
  href?: string
  openInNewTab?: boolean
  pages?: Pages
  isClickable: boolean
}

export default function TilesSection({
  header,
  tiles,
  themeBackgroundColor,
  borderRadiusSize,
  sectionSettings,
}: TilesSectionFlatData): ReactElement<TilesSectionFlatData> {
  const { currentPriceType } = usePriceTypeContext()

  const borderRadiusSizeByType = !isEmpty(borderRadiusSize)
    ? getBorderRadiusSize(borderRadiusSize)
    : undefined

  const getLinkProps = (tile: Tile): CtaLinkProps => {
    if (isEmpty(tile.ctaElements) || tile.ctaElements.length > 1) {
      return { isClickable: false }
    }

    const { ctaType, title } = tile.ctaElements[0].flatData
    const { href, pages, openInNewTab } = getActionTypeData(
      ctaType.actionType,
      currentPriceType
    )

    if (isEmpty(pages) && isEmpty(href)) {
      return { isClickable: false }
    }

    return { title, href, pages, openInNewTab, isClickable: true }
  }

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {!isEmpty(header) && (
          <PushOrg bottomSpace={{ small: 4, medium: 6, large: 10 }}>
            <AlignContentOrg
              horizontalAlign={{
                small: "left",
                medium: "center",
                large: "center",
              }}
            >
              <Header {...header} />
            </AlignContentOrg>
          </PushOrg>
        )}

        <TilesOrg>
          {tiles.map((tile) => {
            const { title, href, pages, openInNewTab, isClickable } =
              getLinkProps(tile)

            return (
              <ItemDescriptionBoxMol
                key={tile.images[0].id}
                backgroundColor={themeBackgroundColor.background}
                image={<Media media={tile.images} />}
                hasImageStickyToBottom
                cssPosition={isClickable ? "relative" : undefined}
                borderRadius={borderRadiusSizeByType || "none"}
              >
                <ConditionalWrapper
                  condition={isClickable}
                  wrapper={(children) => (
                    <TileMolLinkBox
                      to={!isEmpty(pages) ? pages[0].flatData.url : undefined}
                      href={href}
                      title={title as string}
                      openInNewTab={openInNewTab}
                    >
                      {children}
                    </TileMolLinkBox>
                  )}
                >
                  <MarkdownText
                    typography={{ default: "xlarge", bold: "xlarge" }}
                    pushSpace={3}
                  >
                    {tile.title}
                  </MarkdownText>
                </ConditionalWrapper>

                <MarkdownText
                  typography={{ default: "small1", bold: "small3" }}
                  pushSpace={4}
                >
                  {tile.text}
                </MarkdownText>

                {!isEmpty(tile.ctaElements) ? (
                  <CtaElement ctaElement={tile.ctaElements} />
                ) : undefined}
              </ItemDescriptionBoxMol>
            )
          })}
        </TilesOrg>
      </BaseContainer>
    </BaseSection>
  )
}
